import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import bg from '@/assets/bg.jpg';

const Hero: React.FC = () => {
  const navigate = useNavigate();

  const features = ['Consistent characters', 'Multi-character coherence', 'Precise shot control'];

  const handleGetStarted = () => {
    navigate('/admin/login');
  };

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0">
        <img src={bg} />
      </div>

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"
        />
      </div>

      {/* Content */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 xl:px-12 text-center max-w-5xl mx-auto">
        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap items-center justify-center gap-4 mb-8"
        >
          <button
            onClick={handleGetStarted}
            className="px-6 py-2 border border-white/30 rounded-full text-white text-sm font-medium tracking-wide hover:bg-white/10 transition-colors duration-300"
          >
            GET STARTED
          </button>
          <Link
            to="/pricing"
            className="px-6 py-2 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full text-white text-sm font-medium tracking-wide hover:from-orange-600 hover:to-orange-500 transition-all duration-300"
          >
            VIEW PRICING
          </Link>
        </motion.div>

        {/* Main Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mb-8"
        >
          <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black text-white tracking-tight">
            <span className="inline-flex items-center"></span>
          </h1>
        </motion.div>

        {/* Hero Image Placeholder */}
        <div className="relative w-80 h-320 sm:w-64 sm:h-64 md:w-80 md:h-180 mx-auto mb-8"></div>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="text-lg sm:text-xl text-gray-300 mb-4 max-w-2xl mx-auto"
        >
          Make characters truly belong to your story.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-base sm:text-lg text-gray-400 mb-8 max-w-2xl mx-auto"
        >
          Animind is a next-generation AI production system for filmmakers.
        </motion.p>

        {/* Feature Tags */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 mb-6"
        >
          {features.map((feature, index) => (
            <motion.span
              key={feature}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 1 + index * 0.1 }}
              className="text-sm text-gray-400"
            >
              {feature}
              {index < features.length - 1 && <span className="ml-2 sm:ml-4 text-gray-600">·</span>}
            </motion.span>
          ))}
        </motion.div>

        {/* CTA Text */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.3 }}
          className="text-sm text-gray-500"
        >
          Go beyond the limits of mainstream AI
        </motion.p>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent" />
    </section>
  );
};

export default Hero;
